import React, { useState } from "react";
import { Link } from "react-router-dom";
import { SparkleIcon as Sparkle, ArrowRightIcon as ArrowRight, XIcon as X } from "@phosphor-icons/react";

/**
 * SellerWelcomeBanner — first-impression banner for users who arrived
 * via the landing-page hero search with a breed that didn't exist in
 * the catalog (`?intent=seller&breed=X`).
 *
 * Reads `localStorage.signup_seller_intent_breed`. Renders a
 * black-and-gold banner directly above the dashboard headline with
 * a primary CTA "Register your first {breed}" → /dogs/new and a
 * dismiss X. Dismissing clears the localStorage key so the banner
 * doesn't re-render on subsequent dashboard visits.
 *
 * Returns null when there's no intent (the common case), so this
 * component is opt-in by signal — no flicker for normal users.
 */
export default function SellerWelcomeBanner() {
  const [breed, setBreed] = useState(() => {
    try {
      return (localStorage.getItem("signup_seller_intent_breed") || "").trim();
    } catch {
      return "";
    }
  });

  const dismiss = () => {
    try {
      localStorage.removeItem("signup_seller_intent_breed");
    } catch {}
    setBreed("");
  };

  if (!breed) return null;

  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-[#0E0E0E] text-white p-6 sm:p-7 mb-6"
      data-testid="seller-welcome-banner"
    >
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(circle at 80% 30%, rgba(201,162,39,0.20), transparent 55%)",
        }}
      />
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-3 right-3 p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors"
        aria-label="Dismiss welcome banner"
        data-testid="seller-welcome-dismiss"
      >
        <X size={16} weight="bold" />
      </button>

      <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-5">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold mb-2 flex items-center gap-1.5">
            <Sparkle size={12} weight="fill" /> Welcome to Armstrong K9 Registry
          </p>
          <h2 className="font-spectral text-2xl md:text-3xl tracking-tight leading-tight">
            Let's register your first <span className="text-gold">{breed}</span>.
          </h2>
          <p className="text-sm text-white/75 mt-2 max-w-2xl">
            You came here because no verified <strong>{breed}</strong> kennel
            existed yet. Register a dog now to lock in that "first verified"
            status — buyers searching this breed will land on your kennel
            first.
          </p>
        </div>
        <Link to="/dogs/new" className="flex-shrink-0">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-full bg-[#C9A227] hover:bg-[#C9A227]/90 text-ink font-semibold h-11 px-6 whitespace-nowrap transition-colors"
            data-testid="seller-welcome-cta"
          >
            Register your first dog
            <ArrowRight size={14} weight="bold" />
          </button>
        </Link>
      </div>
    </div>
  );
}
